import { useState } from "react";
import { useLang } from "@/lib/lang";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { WHATSAPP_URL, PHONE } from "@/lib/constants";
import { Phone, MessageCircle } from "lucide-react";

export default function Contact() {
  const { t } = useLang();
  const [f, setF] = useState({ name: "", phone: "", email: "", city: "", goal: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.phone || f.phone.trim().length < 6) {
      toast.error("Please enter a valid phone number");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", { ...f, source: "contact" });
      setDone(true);
      toast.success(t.contact.thanks);
      setF({ name: "", phone: "", email: "", city: "", goal: "" });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-5">
          <div className="section-eyebrow">{t.nav.contact}</div>
          <h2 className="mt-3 font-display text-4xl md:text-5xl leading-tight" style={{ color: "var(--primary)" }}>
            {t.contact.title}
          </h2>
          <p className="mt-4 text-lg" style={{ color: "var(--text-secondary)" }}>{t.contact.sub}</p>

          <div className="mt-10 space-y-4">
            <a href={`tel:${PHONE}`} data-testid="contact-call" className="flex items-center gap-4 p-5 card-soft transition-transform hover:-translate-y-1">
              <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: "var(--bg-paper)", color: "var(--primary)" }}>
                <Phone size={20} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest" style={{ color: "var(--text-secondary)" }}>{t.contact.phone_direct}</div>
                <div className="font-semibold text-lg" style={{ color: "var(--primary)" }}>+1 437-557-1041</div>
              </div>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="flex items-center gap-4 p-5 card-soft transition-transform hover:-translate-y-1">
              <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: "#25D366", color: "white" }}>
                <MessageCircle size={20} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest" style={{ color: "var(--text-secondary)" }}>WhatsApp</div>
                <div className="font-semibold text-lg" style={{ color: "var(--primary)" }}>Chat instantly</div>
              </div>
            </a>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="card-soft p-8 md:p-10">
            {done ? (
              <div data-testid="contact-thanks" className="text-center py-8">
                <div className="font-display text-3xl" style={{ color: "var(--primary)" }}>ਸ਼ੁਕਰੀਆ! 🙏</div>
                <p className="mt-3" style={{ color: "var(--text-secondary)" }}>{t.contact.thanks}</p>
                <button onClick={() => setDone(false)} className="btn-ghost mt-6">Send another</button>
              </div>
            ) : (
              <form onSubmit={submit} className="grid md:grid-cols-2 gap-5">
                <Field label={t.contact.name}>
                  <Input data-testid="contact-name" value={f.name} onChange={set("name")} className="h-12 rounded-xl" />
                </Field>
                <Field label={t.contact.phone + " *"}>
                  <Input data-testid="contact-phone" required type="tel" value={f.phone} onChange={set("phone")} className="h-12 rounded-xl" placeholder="+91 98xxx xxxxx" />
                </Field>
                <Field label={t.contact.email}>
                  <Input data-testid="contact-email" type="email" value={f.email} onChange={set("email")} className="h-12 rounded-xl" />
                </Field>
                <Field label={t.contact.city}>
                  <Input data-testid="contact-city" value={f.city} onChange={set("city")} className="h-12 rounded-xl" placeholder="Brampton / Chandigarh / ..." />
                </Field>
                <div className="md:col-span-2">
                  <Field label={t.contact.goal}>
                    <Input data-testid="contact-goal" value={f.goal} onChange={set("goal")} className="h-12 rounded-xl" />
                  </Field>
                </div>
                <div className="md:col-span-2">
                  <button data-testid="contact-submit" disabled={loading} className="btn-primary w-full md:w-auto h-12 disabled:opacity-60">
                    {loading ? "..." : t.contact.submit}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--text-secondary)" }}>{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
